"use client"

import { useState, useCallback, useMemo } from "react"
import { getDatabase, ref, remove } from "firebase/database"
import { displayQuantityWithUnit } from "./Utils"
import { useCurrentUid } from "../hooks/useCurrentUid"

export default function PreviousHistory({
  historyQuotes,
  loading,
  setActiveQuote,
  toggleHistory,
  isMobile,
  activeSession,
  showHistory,
}) {
  const userId = useCurrentUid()
  const [deleteMode, setDeleteMode] = useState(false)
  const [selectedForDeletion, setSelectedForDeletion] = useState({})
  const [expandedHistory, setExpandedHistory] = useState({})
  const [confirmDelete, setConfirmDelete] = useState(false)
  const [isDeleting, setIsDeleting] = useState(false)

  const toggleDeleteMode = useCallback(() => {
    setDeleteMode((prev) => !prev)
    if (deleteMode) {
      setSelectedForDeletion({})
      setConfirmDelete(false)
    }
  }, [deleteMode])

  const toggleSelectForDeletion = useCallback((quoteId) => {
    setSelectedForDeletion((prev) => ({
      ...prev,
      [quoteId]: !prev[quoteId],
    }))
  }, [])

  const handleDeleteSelected = useCallback(async () => {
    if (!confirmDelete) {
      setConfirmDelete(true)
      return
    }

    if (isDeleting) return

    const selectedIds = Object.keys(selectedForDeletion).filter((id) => selectedForDeletion[id])

    if (selectedIds.length === 0) {
      alert("No quotations selected for deletion")
      return
    }

    setIsDeleting(true)

    try {
      const db = getDatabase()
      const deletePromises = selectedIds.map((quoteId) => {
        const quoteRef = ref(db, `quoteRequests/${userId}/${quoteId}`)
        return remove(quoteRef)
      })

      await Promise.all(deletePromises)

      setSelectedForDeletion({})
      setDeleteMode(false)
      setConfirmDelete(false)
      alert("Selected quotations deleted successfully")
    } catch (error) {
      console.error("Error deleting quotations:", error)
      alert("Error deleting quotations: " + error.message)
    } finally {
      setIsDeleting(false)
    }
  }, [confirmDelete, selectedForDeletion, userId, isDeleting])

  const toggleExpandHistory = useCallback(
    (quoteId, e) => {
      if (deleteMode) {
        e.stopPropagation()
        toggleSelectForDeletion(quoteId)
        return
      }

      setExpandedHistory((prev) => ({
        ...prev,
        [quoteId]: !prev[quoteId],
      }))
    },
    [deleteMode, toggleSelectForDeletion]
  )

  const copyToClipboard = useCallback((quote) => {
    let text = `QUOTATION DETAILS\n\n`
    text += `Category: ${quote.category || "Cement"}\n`
    text += `Brand: ${quote.brand || quote.financeType || "N/A"}\n`

    if (quote.category === "finance") {
      text += `Finance Type: ${quote.financeType}\n`
      text += `Loan Amount: ₹${quote.loanAmount}\n`
      text += `Project Description: ${quote.projectDescription || "N/A"}\n`
    } else if (quote.category === "cement" || !quote.category) {
      text += `Grade: ${quote.grade}\n`
      text += `Quantity: ${quote.quantity} ${quote.cementType === "bulker" ? "MT" : "bags"}\n`
      text += `Cement Type: ${quote.cementType || "bags"}\n`
    } else if (quote.category === "steel") {
      text += `Steel Grade: ${quote.steelGrade}\n`
      text += `Steel Type: ${quote.steelType}\n`
      text += `Quantity: ${quote.quantity} ${quote.quantityUnit || "tons"}\n`
    } else if (quote.category === "machinery") {
      text += `Machinery Type: ${quote.machineryType}\n`
      text += `Capacity: ${quote.machineryCapacity}\n`
      text += `Condition: ${quote.machineryCondition}\n`
      text += `Quantity: ${quote.quantity} ${quote.quantityUnit || "units"}\n`
    }

    text += `Site Address: ${quote.siteAddress}\n\n`
    text += `Contact Information:\n`
    text += `Name: ${quote.contactInfo?.name || "N/A"}\n`
    text += `Phone: ${quote.contactInfo?.phone || "N/A"}\n`

    if (quote.supplierReply) {
      text += `\nSUPPLIER'S RESPONSE\n\n`
      text += `Price: ₹${quote.supplierReply.pricePerBag || quote.supplierReply.pricePerUnit || "N/A"}\n`
      text += `Total: ₹${quote.supplierReply.totalPrice || "N/A"}\n`
      text += `Message: "${quote.supplierReply.message || ""}"\n`
    }

    navigator.clipboard.writeText(text)
      .then(() => alert("Quotation details copied to clipboard!"))
      .catch(() => alert("Failed to copy"))
  }, [])

  const selectedCount = useMemo(() => {
    return Object.values(selectedForDeletion).filter(Boolean).length
  }, [selectedForDeletion])

  return (
    <div className={`history-panel ${showHistory ? "mobile-history" : ""}`}>
      <div className="history-panel__header">
        <h3 className="history-panel__title">Previous Quotations</h3>
        <div className="history-panel__actions">
          <button
            className={`button button--icon ${deleteMode ? "button--active" : ""}`}
            onClick={toggleDeleteMode}
            title="Delete Mode"
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <polyline points="3,6 5,6 21,6"></polyline>
              <path d="M19,6v14a2,2,0,0,1-2,2H7a2,2,0,0,1-2-2V6m3,0V4a2,2,0,0,1,2-2h4a2,2,0,0,1,2,2V6"></path>
            </svg>
          </button>
          {isMobile && (
            <button className="button button--icon" onClick={toggleHistory}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <polyline points="15,18 9,12 15,6"></polyline>
              </svg>
            </button>
          )}
        </div>
      </div>

      <div className="history-panel__content">
        {deleteMode && (
          <div className="delete-controls">
            <p className="delete-info">
              {selectedCount} item{selectedCount !== 1 ? "s" : ""} selected
            </p>
            <button
              onClick={handleDeleteSelected}
              className={`button ${confirmDelete ? "button--danger" : "button--outline"} button--full`}
              disabled={isDeleting || selectedCount === 0}
            >
              {isDeleting ? "Deleting..." : confirmDelete ? "Confirm Delete" : "Delete Selected"}
            </button>
          </div>
        )}

        {loading ? (
          <div className="loading">
            <div className="loading__spinner"></div>
            <p>Loading history...</p>
          </div>
        ) : historyQuotes.length === 0 ? (
          <div className="alert alert--info">
            <p>No previous quotations found.</p>
          </div>
        ) : (
          <div className="history-list">
            {historyQuotes.map((request) => (
              <div
                key={request.id}
                className={`history-item ${expandedHistory[request.id] ? "history-item--expanded" : ""} ${
                  selectedForDeletion[request.id] ? "history-item--selected" : ""
                }`}
                onClick={(e) => toggleExpandHistory(request.id, e)}
              >
                {deleteMode && (
                  <div className="history-item__checkbox">
                    <input
                      type="checkbox"
                      checked={!!selectedForDeletion[request.id]}
                      onChange={() => toggleSelectForDeletion(request.id)}
                      onClick={(e) => e.stopPropagation()}
                    />
                  </div>
                )}

                <div className="history-item__header">
                  <div className="history-item__title-area">
                    <h4 className="history-item__title">
                      {request.category === "finance"
                        ? `Finance - ${request.financeType || "Loan Request"}`
                        : `${request.category || "Cement"} - ${request.brand} - ${
                            request.grade || request.steelGrade || request.machineryType || ""
                          }`}
                    </h4>
                    <p className="history-item__date">{request.timestamp || request.submittedAt}</p>
                  </div>

                  <div className={`badge badge--small ${request.supplierReply ? "badge--success" : "badge--secondary"}`}>
                    {request.supplierReply ? "Responded" : "Pending"}
                  </div>
                </div>

                {expandedHistory[request.id] && (
                  <div className="history-item__details">
                    <div className="history-item__section">
                      {request.category === "finance" ? (
                        <>
                          <p>
                            <span className="history-item__label">Finance Type:</span> {request.financeType}
                          </p>
                          <p>
                            <span className="history-item__label">Loan Amount:</span> ₹{request.loanAmount}
                          </p>
                          <p>
                            <span className="history-item__label">Project Description:</span> 
                            {request.projectDescription ? request.projectDescription.substring(0, 120) + "..." : "N/A"}
                          </p>
                        </>
                      ) : request.category === "cement" || !request.category ? (
                        <>
                          <p><span className="history-item__label">Brand:</span> {request.brand}</p>
                          <p><span className="history-item__label">Grade:</span> {request.grade}</p>
                          <p><span className="history-item__label">Type:</span> {request.cementType || "bags"}</p>
                          <p><span className="history-item__label">Quantity:</span> {displayQuantityWithUnit(request)}</p>
                        </>
                      ) : request.category === "steel" ? (
                        <>
                          <p><span className="history-item__label">Brand:</span> {request.brand}</p>
                          <p><span className="history-item__label">Steel Grade:</span> {request.steelGrade}</p>
                          <p><span className="history-item__label">Steel Type:</span> {request.steelType}</p>
                          <p><span className="history-item__label">Quantity:</span> {displayQuantityWithUnit(request)}</p>
                        </>
                      ) : request.category === "machinery" ? (
                        <>
                          <p><span className="history-item__label">Brand:</span> {request.brand}</p>
                          <p><span className="history-item__label">Machinery Type:</span> {request.machineryType}</p>
                          <p><span className="history-item__label">Capacity:</span> {request.machineryCapacity}</p>
                          <p><span className="history-item__label">Condition:</span> {request.machineryCondition}</p>
                          <p><span className="history-item__label">Quantity:</span> {displayQuantityWithUnit(request)}</p>
                        </>
                      ) : null}

                      <p>
                        <span className="history-item__label">Site Address:</span> {request.siteAddress}
                      </p>

                      {request.supplierReply && (
                        <div className="history-item__response">
                          <p>
                            <span className="history-item__label">Price:</span> ₹
                            {request.supplierReply.pricePerBag || request.supplierReply.pricePerUnit}/
                            {request.category === "cement" || !request.category
                              ? request.cementType === "bulker" ? "MT" : "bag"
                              : request.category === "steel" ? "ton" : "unit"}
                          </p>
                          <p>
                            <span className="history-item__label">Total:</span> ₹{request.supplierReply.totalPrice}
                          </p>
                        </div>
                      )}
                    </div>

                    <div className="history-item__actions">
                      <button
                        onClick={(e) => { e.stopPropagation(); copyToClipboard(request); }}
                        className="button button--small button--outline"
                        title="Copy quotation details"
                      >
                        Copy
                      </button>
                      <button
                        onClick={(e) => { e.stopPropagation(); setActiveQuote(request); }}
                        className="button button--small button--primary"
                      >
                        Open Session
                      </button>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  )
}